/**
 * AdminCustomProgramEdit - Editar rutina personalizada
 * 
 * Features:
 * - Configurar días de entrenamiento
 * - Añadir/editar/eliminar ejercicios
 * - Drag & drop para reordenar
 * - Buscar ejercicios de la biblioteca
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { toast } from "sonner";
import {
  Plus,
  Search,
  ArrowLeft,
  Save,
  Trash2,
  GripVertical,
  Dumbbell,
  Clock,
  Hash,
  Target,
  Edit,
  X,
  ChevronDown,
  Calendar,
  Loader2,
  User,
  Home,
  Building2,
  CheckCircle2
} from "lucide-react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INTENSITY_OPTIONS = [
  { value: "low", label: "Baja" },
  { value: "moderate", label: "Moderada" },
  { value: "high", label: "Alta" },
  { value: "max", label: "Máxima" }
];

const OBJECTIVES = {
  hypertrophy: "Hipertrofia",
  strength: "Fuerza",
  definition: "Definición",
  health: "Salud",
  performance: "Rendimiento",
  rehabilitation: "Rehabilitación"
};

const AdminCustomProgramEdit = () => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [program, setProgram] = useState(null);
  const [activeDay, setActiveDay] = useState(null);
  
  // Exercise library
  const [exercises, setExercises] = useState([]);
  const [exerciseSearch, setExerciseSearch] = useState("");
  const [showExerciseDialog, setShowExerciseDialog] = useState(false);
  const [selectedDayForExercise, setSelectedDayForExercise] = useState(null);
  
  // Day dialog
  const [showDayDialog, setShowDayDialog] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [dayForm, setDayForm] = useState({
    day_number: 1,
    name: "",
    focus: "",
    notes: ""
  });
  
  // Exercise edit dialog
  const [showExerciseEditDialog, setShowExerciseEditDialog] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);
  const [exerciseForm, setExerciseForm] = useState({
    name: "",
    sets: 3,
    reps: "10-12",
    rest_seconds: 90,
    rir: "",
    intensity: "moderate",
    technique_notes: ""
  });

  // Fetch program data
  const fetchProgram = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/custom-programs/${programId}`,
        { headers: getAuthHeaders() }
      );
      setProgram(response.data);
      
      // Set first day as active
      if (response.data.days?.length > 0) {
        setActiveDay(response.data.days[0].id);
      }
    } catch (error) {
      console.error("Error fetching program:", error);
      toast.error("Error cargando rutina");
      navigate("/admin/custom-programs");
    } finally {
      setLoading(false);
    }
  }, [programId, getAuthHeaders, navigate]);

  // Fetch exercise library
  const fetchExercises = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/exercise-library`,
        { headers: getAuthHeaders() }
      );
      setExercises(response.data.exercises || response.data || []);
    } catch (error) {
      console.error("Error fetching exercises:", error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchProgram();
    fetchExercises();
  }, [fetchProgram, fetchExercises]);

  // Add day
  const handleAddDay = async () => {
    if (!dayForm.name.trim()) {
      toast.error("El nombre del día es obligatorio");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/api/custom-programs/${programId}/days`,
        {
          day_number: dayForm.day_number,
          name: dayForm.name,
          focus: dayForm.focus,
          notes: dayForm.notes,
          exercises: []
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success("Día añadido");
      setShowDayDialog(false);
      setDayForm({ day_number: (program?.days?.length || 0) + 1, name: "", focus: "", notes: "" });
      fetchProgram();
    } catch (error) {
      console.error("Error adding day:", error);
      toast.error("Error al añadir día");
    } finally {
      setSaving(false);
    }
  };

  // Update day
  const handleUpdateDay = async () => {
    if (!editingDay) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/custom-programs/days/${editingDay.id}`,
        {
          name: dayForm.name,
          focus: dayForm.focus,
          notes: dayForm.notes
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success("Día actualizado");
      setShowDayDialog(false);
      setEditingDay(null);
      fetchProgram();
    } catch (error) {
      console.error("Error updating day:", error);
      toast.error("Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  // Delete day
  const handleDeleteDay = async (dayId) => {
    if (!window.confirm("¿Eliminar este día y todos sus ejercicios?")) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/custom-programs/days/${dayId}`,
        { headers: getAuthHeaders() }
      );
      toast.success("Día eliminado");
      fetchProgram();
    } catch (error) {
      console.error("Error deleting day:", error);
      toast.error("Error al eliminar");
    }
  };

  // Add exercise to day
  const handleAddExercise = async (exerciseId, exerciseName) => {
    if (!selectedDayForExercise) return;
    
    const existingCount = program?.days?.find(d => d.id === selectedDayForExercise)?.exercises?.length || 0;
    
    try {
      await axios.post(
        `${API_URL}/api/custom-programs/days/${selectedDayForExercise}/exercises`,
        {
          exercise_id: exerciseId,
          name: exerciseName || "",  // El nombre se tomará de la biblioteca si está vacío
          sort_order: existingCount + 1,
          sets: 3,
          reps: "10-12",
          rest_seconds: 90,
          rir: "",
          intensity: "moderate",
          technique_notes: ""
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success("Ejercicio añadido");
      setShowExerciseDialog(false);
      fetchProgram();
    } catch (error) {
      console.error("Error adding exercise:", error);
      toast.error("Error al añadir ejercicio");
    }
  };

  // Update exercise
  const handleUpdateExercise = async () => {
    if (!editingExercise) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/custom-programs/exercises/${editingExercise.id}`,
        exerciseForm,
        { headers: getAuthHeaders() }
      );
      
      toast.success("Ejercicio actualizado");
      setShowExerciseEditDialog(false);
      setEditingExercise(null);
      fetchProgram();
    } catch (error) {
      console.error("Error updating exercise:", error);
      toast.error("Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  // Delete exercise
  const handleDeleteExercise = async (exerciseId) => {
    if (!window.confirm("¿Eliminar este ejercicio?")) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/custom-programs/exercises/${exerciseId}`,
        { headers: getAuthHeaders() }
      );
      toast.success("Ejercicio eliminado");
      fetchProgram();
    } catch (error) {
      console.error("Error deleting exercise:", error);
      toast.error("Error al eliminar");
    }
  };

  // Filter exercises
  const filteredExercises = exercises.filter(ex =>
    ex.name.toLowerCase().includes(exerciseSearch.toLowerCase()) ||
    ex.muscle_group?.toLowerCase().includes(exerciseSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center text-white">
        <p>Rutina no encontrada</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#0B0B0B]/95 backdrop-blur border-b border-[#1A1A1A] px-4 py-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin/custom-programs")}
            className="text-white hover:bg-[#1A1A1A]"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-heading font-bold text-[#D4AF37] truncate">
              {program.name}
            </h1>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {program.client && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {program.client.email}
                </span>
              )}
              {program.is_template && (
                <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] text-xs">
                  Plantilla
                </Badge>
              )}
              {program.is_active && (
                <Badge className="bg-green-500/20 text-green-400 text-xs">
                  Activa
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Program Info */}
      <div className="p-4">
        <Card className="bg-[#1A1A1A] border-[#2A2A2A] mb-4">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Objetivo:</span>
                <span className="ml-2 text-white">{OBJECTIVES[program.objective]}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Lugar:</span>
                <span className="ml-2 text-white">
                  {program.location === "gym" ? "Gimnasio" : program.location === "home" ? "Casa" : "Mixto"}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Días/semana:</span>
                <span className="ml-2 text-white">{program.days_per_week}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Días creados:</span>
                <span className="ml-2 text-white">{program.days?.length || 0}</span>
              </div>
            </div>
            {program.coach_notes && (
              <div className="mt-3 pt-3 border-t border-[#2A2A2A]">
                <p className="text-xs text-muted-foreground mb-1">Notas:</p>
                <p className="text-sm text-white">{program.coach_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Days Section */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-white">Días de Entrenamiento</h2>
          <Button
            size="sm"
            onClick={() => {
              setEditingDay(null);
              setDayForm({
                day_number: (program.days?.length || 0) + 1,
                name: `Día ${(program.days?.length || 0) + 1}`,
                focus: "",
                notes: ""
              });
              setShowDayDialog(true);
            }}
            className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]"
          >
            <Plus className="w-4 h-4 mr-1" />
            Añadir Día
          </Button>
        </div>
        
        {/* Days Accordion */}
        {program.days?.length === 0 ? (
          <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
            <CardContent className="p-8 text-center">
              <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No hay días configurados</p>
              <p className="text-xs text-muted-foreground mt-1">Añade días de entrenamiento para esta rutina</p>
            </CardContent>
          </Card>
        ) : (
          <Accordion type="single" collapsible value={activeDay} onValueChange={setActiveDay}>
            {program.days.map((day, index) => (
              <AccordionItem 
                key={day.id} 
                value={day.id}
                className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg mb-2 overflow-hidden"
              >
                <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-[#2A2A2A]/50">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-8 h-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-bold text-sm">
                      {day.day_number}
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-white">{day.name}</p>
                      {day.focus && (
                        <p className="text-xs text-muted-foreground">{day.focus}</p>
                      )}
                    </div>
                    <Badge className="ml-auto mr-2 bg-[#2A2A2A] text-white">
                      {day.exercises?.length || 0} ejercicios
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4">
                  {/* Day actions */}
                  <div className="flex gap-2 mb-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditingDay(day);
                        setDayForm({
                          day_number: day.day_number,
                          name: day.name,
                          focus: day.focus || "",
                          notes: day.notes || ""
                        });
                        setShowDayDialog(true);
                      }}
                      className="border-[#2A2A2A] text-white"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Editar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteDay(day.id)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Eliminar
                    </Button>
                  </div>
                  
                  {/* Exercises */}
                  <div className="space-y-2">
                    {day.exercises?.map((exercise, exIndex) => (
                      <div
                        key={exercise.id}
                        className="flex items-center gap-3 p-3 bg-[#0B0B0B] rounded-lg border border-[#2A2A2A]"
                      >
                        <div className="text-muted-foreground">
                          <GripVertical className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white truncate">
                            {exercise.name || exercise.exercise?.name || "Nombre del ejercicio"}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Hash className="w-3 h-3" />
                              {exercise.sets} × {exercise.reps}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {exercise.rest_seconds}s
                            </span>
                            {exercise.rir && (
                              <span>RIR {exercise.rir}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => {
                              setEditingExercise(exercise);
                              setExerciseForm({
                                name: exercise.name || exercise.exercise?.name || "",
                                sets: exercise.sets,
                                reps: exercise.reps,
                                rest_seconds: exercise.rest_seconds,
                                rir: exercise.rir || "",
                                intensity: exercise.intensity || "moderate",
                                technique_notes: exercise.technique_notes || ""
                              });
                              setShowExerciseEditDialog(true);
                            }}
                            className="h-8 w-8 text-white hover:bg-[#2A2A2A]"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDeleteExercise(exercise.id)}
                            className="h-8 w-8 text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Add exercise button */}
                  <Button
                    variant="outline"
                    className="w-full mt-3 border-dashed border-[#2A2A2A] text-muted-foreground hover:text-white hover:border-[#D4AF37]"
                    onClick={() => {
                      setSelectedDayForExercise(day.id);
                      setExerciseSearch("");
                      setShowExerciseDialog(true);
                    }}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Añadir Ejercicio
                  </Button>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>
      
      {/* Day Dialog */}
      <Dialog open={showDayDialog} onOpenChange={setShowDayDialog}>
        <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37]">
              {editingDay ? "Editar Día" : "Añadir Día"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre del día</Label>
              <Input
                value={dayForm.name}
                onChange={(e) => setDayForm({ ...dayForm, name: e.target.value })}
                placeholder="Ej: Push - Pecho y Tríceps"
                className="bg-[#0B0B0B] border-[#2A2A2A] text-white"
              />
            </div>
            
            <div>
              <Label>Enfoque muscular (opcional)</Label>
              <Input
                value={dayForm.focus}
                onChange={(e) => setDayForm({ ...dayForm, focus: e.target.value })}
                placeholder="Ej: Pecho, Hombros, Tríceps"
                className="bg-[#0B0B0B] border-[#2A2A2A] text-white"
              />
            </div>
            
            <div>
              <Label>Notas (opcional)</Label>
              <Textarea
                value={dayForm.notes}
                onChange={(e) => setDayForm({ ...dayForm, notes: e.target.value })}
                placeholder="Indicaciones especiales para este día..."
                className="bg-[#0B0B0B] border-[#2A2A2A] text-white"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDayDialog(false)}
              className="border-[#2A2A2A] text-white"
            >
              Cancelar
            </Button>
            <Button
              onClick={editingDay ? handleUpdateDay : handleAddDay}
              disabled={saving}
              className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingDay ? "Guardar" : "Añadir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Exercise Search Dialog */}
      <Dialog open={showExerciseDialog} onOpenChange={setShowExerciseDialog}>
        <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37]">
              Añadir Ejercicio
            </DialogTitle>
          </DialogHeader>
          
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={exerciseSearch}
              onChange={(e) => setExerciseSearch(e.target.value)}
              placeholder="Buscar ejercicio..."
              className="pl-10 bg-[#0B0B0B] border-[#2A2A2A] text-white"
            />
          </div>
          
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {filteredExercises.slice(0, 30).map(exercise => (
              <button
                key={exercise.id}
                onClick={() => handleAddExercise(exercise.id, exercise.name)}
                className="w-full flex items-center gap-3 p-3 bg-[#0B0B0B] rounded-lg border border-[#2A2A2A] hover:border-[#D4AF37] transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-lg bg-[#2A2A2A] flex items-center justify-center">
                  <Dumbbell className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white truncate">{exercise.name}</p>
                  <p className="text-xs text-muted-foreground">{exercise.muscle_group}</p>
                </div>
                <Plus className="w-5 h-5 text-[#D4AF37]" />
              </button>
            ))}
            
            {filteredExercises.length === 0 && (
              <p className="text-center text-muted-foreground py-8">
                No se encontraron ejercicios
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Exercise Edit Dialog */}
      <Dialog open={showExerciseEditDialog} onOpenChange={setShowExerciseEditDialog}>
        <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white">
          <DialogHeader>
            <DialogTitle className="text-[#D4AF37]">
              Editar Ejercicio
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Nombre del ejercicio */}
            <div>
              <Label>Nombre del ejercicio</Label>
              <Input
                value={exerciseForm.name}
                onChange={(e) => setExerciseForm({ ...exerciseForm, name: e.target.value })}
                placeholder="Nombre del ejercicio"
                className="bg-[#0B0B0B] border-[#2A2A2A] text-white"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Series</Label>
                <Input
                  type="number"
                  value={exerciseForm.sets}
                  onChange={(e) => setExerciseForm({ ...exerciseForm, sets: parseInt(e.target.value) || 3 })}
                  min={1}
                  max={10}
                  className="bg-[#0B0B0B] border-[#2A2A2A] text-white"
                />
              </div>
              <div>
                <Label>Repeticiones</Label>
                <Input
                  value={exerciseForm.reps}
                  onChange={(e) => setExerciseForm({ ...exerciseForm, reps: e.target.value })}
                  placeholder="8-10, 12, AMRAP"
                  className="bg-[#0B0B0B] border-[#2A2A2A] text-white"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Descanso (seg)</Label>
                <Input
                  type="number"
                  value={exerciseForm.rest_seconds}
                  onChange={(e) => setExerciseForm({ ...exerciseForm, rest_seconds: parseInt(e.target.value) || 60 })}
                  min={0}
                  max={300}
                  className="bg-[#0B0B0B] border-[#2A2A2A] text-white"
                />
              </div>
              <div>
                <Label>RIR (opcional)</Label>
                <Input
                  value={exerciseForm.rir}
                  onChange={(e) => setExerciseForm({ ...exerciseForm, rir: e.target.value })}
                  placeholder="0-1, 2-3"
                  className="bg-[#0B0B0B] border-[#2A2A2A] text-white"
                />
              </div>
            </div>
            
            <div>
              <Label>Intensidad</Label>
              <Select
                value={exerciseForm.intensity}
                onValueChange={(value) => setExerciseForm({ ...exerciseForm, intensity: value })}
              >
                <SelectTrigger className="bg-[#0B0B0B] border-[#2A2A2A] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                  {INTENSITY_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-white">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Notas técnicas (opcional)</Label>
              <Textarea
                value={exerciseForm.technique_notes}
                onChange={(e) => setExerciseForm({ ...exerciseForm, technique_notes: e.target.value })}
                placeholder="Indicaciones de ejecución, tempo, etc."
                className="bg-[#0B0B0B] border-[#2A2A2A] text-white"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowExerciseEditDialog(false)}
              className="border-[#2A2A2A] text-white"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpdateExercise}
              disabled={saving}
              className="bg-[#D4AF37] hover:bg-[#E5C54D] text-[#0B0B0B]"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCustomProgramEdit;
